import React, { useEffect, useState } from "react";
import { RefreshCcw, Briefcase, Plus, Trash2, X, KeyRound } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const EMPTY = { company_name: "", contact_name: "", email: "", password: "", phone: "" };
const fmtSAR = (n) => `${Number(n || 0).toLocaleString("tr-TR")} SAR`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("tr-TR"); } catch { return iso || "—"; } };

const AdminCompanies = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [pwFor, setPwFor] = useState(null);
    const [newPw, setNewPw] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/company-accounts");
            setItems(data);
        } catch { toast.error("Şirket hesapları yüklenemedi"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post("/admin/company-accounts", form);
            toast.success("Şirket hesabı oluşturuldu. Kullanıcı adı ve şifreyi şirkete iletin.");
            setShowForm(false);
            setForm(EMPTY);
            load();
        } catch (err) { toast.error(err.response?.data?.detail || "Oluşturulamadı"); }
        finally { setSaving(false); }
    };

    const toggleActive = async (c) => {
        try {
            await api.put(`/admin/company-accounts/${c.id}`, { active: c.active === false });
            setItems((cur) => cur.map((x) => (x.id === c.id ? { ...x, active: c.active === false } : x)));
            toast.success(c.active === false ? "Hesap aktifleştirildi" : "Hesap pasife alındı");
        } catch { toast.error("Güncellenemedi"); }
    };

    const resetPassword = async () => {
        if (!pwFor || newPw.length < 6) { toast.error("Şifre en az 6 karakter olmalı"); return; }
        try {
            await api.put(`/admin/company-accounts/${pwFor.id}`, { password: newPw });
            toast.success(`${pwFor.company_name} için yeni şifre kaydedildi`);
            setPwFor(null);
            setNewPw("");
        } catch { toast.error("Şifre güncellenemedi"); }
    };

    const remove = async (c) => {
        if (!window.confirm(`"${c.company_name}" hesabı silinsin mi?`)) return;
        try {
            await api.delete(`/admin/company-accounts/${c.id}`);
            setItems((cur) => cur.filter((x) => x.id !== c.id));
            toast.success("Silindi");
        } catch { toast.error("Silinemedi"); }
    };

    return (
        <div>
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                <div>
                    <h1 className="font-serif text-4xl mb-2">Şirket Hesapları</h1>
                    <p className="text-muted_green text-sm">Şirket/toplu alım fiyatlarını yalnızca bu hesaplar görür. Hesabı oluşturun, e-posta + şifreyi şirkete iletin — sitedeki normal giriş ekranından giriş yaparlar.</p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={load} className="btn-outline-gold !text-xs" data-testid="refresh-companies"><RefreshCcw className="w-3.5 h-3.5" /> Yenile</button>
                    <button onClick={() => { setShowForm(true); setForm(EMPTY); }} className="btn-gold !text-xs" data-testid="new-company-btn"><Plus className="w-3.5 h-3.5" /> Yeni Şirket</button>
                </div>
            </div>

            {showForm && (
                <form onSubmit={save} className="border border-gold/40 bg-emerald_surface p-6 mb-8 space-y-4" data-testid="company-form">
                    <div className="flex items-center justify-between">
                        <h2 className="font-serif text-2xl">Yeni Şirket Hesabı</h2>
                        <button type="button" onClick={() => setShowForm(false)} className="text-muted_green hover:text-gold"><X className="w-5 h-5" /></button>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                            <label className="label-dark">Şirket Adı *</label>
                            <input required type="text" className="input-dark" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="company-name-input" />
                        </div>
                        <div>
                            <label className="label-dark">Yetkili Kişi</label>
                            <input type="text" className="input-dark" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
                        </div>
                        <div>
                            <label className="label-dark">Telefon</label>
                            <input type="tel" className="input-dark" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                        </div>
                        <div>
                            <label className="label-dark">Giriş E-postası (kullanıcı adı) *</label>
                            <input required type="email" className="input-dark" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="company-email-input" />
                        </div>
                        <div>
                            <label className="label-dark">Şifre * (min 6)</label>
                            <input required type="text" minLength={6} className="input-dark" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="company-password-input" />
                        </div>
                    </div>
                    <button type="submit" disabled={saving} className="btn-gold !text-sm" data-testid="save-company-btn">
                        {saving ? "Kaydediliyor…" : "Hesabı Oluştur"}
                    </button>
                </form>
            )}

            {pwFor && (
                <div className="border border-gold/40 bg-emerald_surface p-5 mb-6 flex items-end gap-3 flex-wrap" data-testid="password-reset-box">
                    <div className="flex-1 min-w-[220px]">
                        <label className="label-dark">"{pwFor.company_name}" için yeni şifre</label>
                        <input type="text" className="input-dark" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Min 6 karakter" data-testid="new-password-input" />
                    </div>
                    <button onClick={resetPassword} className="btn-gold !text-xs" data-testid="confirm-password-btn">Şifreyi Kaydet</button>
                    <button onClick={() => { setPwFor(null); setNewPw(""); }} className="btn-outline-gold !text-xs">İptal</button>
                </div>
            )}

            {loading ? (
                <div className="text-muted_green text-sm">Yükleniyor…</div>
            ) : items.length === 0 ? (
                <div className="border border-dashed border-border_green p-12 text-center">
                    <Briefcase className="w-14 h-14 text-muted_green mx-auto mb-3" />
                    <div className="text-muted_green text-sm">Henüz şirket hesabı yok. "Yeni Şirket" ile oluşturun.</div>
                </div>
            ) : (
                <div className="border border-border_green bg-emerald_surface overflow-x-auto">
                    <table className="w-full text-sm" data-testid="companies-table">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-muted_green border-b border-border_green">
                                <th className="px-5 py-3">Şirket</th>
                                <th className="px-5 py-3">Giriş E-postası</th>
                                <th className="px-5 py-3">Telefon</th>
                                <th className="px-5 py-3">Rezervasyon</th>
                                <th className="px-5 py-3">Toplam Hacim</th>
                                <th className="px-5 py-3">Durum</th>
                                <th className="px-5 py-3">Kayıt</th>
                                <th className="px-5 py-3 text-right">İşlem</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border_green">
                            {items.map((c) => (
                                <tr key={c.id} data-testid={`company-row-${c.id}`}>
                                    <td className="px-5 py-3">
                                        <div className="font-semibold text-gold">{c.company_name || "—"}</div>
                                        {c.name && <div className="text-[11px] text-muted_green">{c.name}</div>}
                                    </td>
                                    <td className="px-5 py-3 font-mono text-xs">{c.email}</td>
                                    <td className="px-5 py-3 text-xs">{c.phone || "—"}</td>
                                    <td className="px-5 py-3">{c.reservation_count || 0}</td>
                                    <td className="px-5 py-3 text-gold">{fmtSAR(c.reservation_total)}</td>
                                    <td className="px-5 py-3">
                                        <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] border ${c.active !== false ? "text-emerald-300 border-emerald-500/60 bg-emerald-900/30" : "text-rose-300 border-rose-500/50 bg-rose-900/20"}`}>
                                            {c.active !== false ? "Aktif" : "Pasif"}
                                        </span>
                                    </td>
                                    <td className="px-5 py-3 text-xs text-muted_green">{fmtDate(c.created_at)}</td>
                                    <td className="px-5 py-3">
                                        <div className="flex items-center justify-end gap-1.5">
                                            <button onClick={() => { setPwFor(c); setNewPw(""); }} title="Şifre sıfırla" className="text-muted_green hover:text-gold p-1.5" data-testid={`pw-${c.id}`}><KeyRound className="w-4 h-4" /></button>
                                            <button onClick={() => toggleActive(c)} className="btn-outline-gold !text-[10px] !px-2 !py-1" data-testid={`toggle-${c.id}`}>
                                                {c.active !== false ? "Pasife Al" : "Aktifleştir"}
                                            </button>
                                            <button onClick={() => remove(c)} className="text-rose-400 hover:text-rose-500 p-1.5" data-testid={`del-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default AdminCompanies;
